import { FC, useCallback, useEffect, useState } from "react";
import { Snackbar } from "@sunrun/experience-ui-components";
import { SplatContact } from "../../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { SplatProspectResponse } from "../../../../amplify/backend/function/OfferExpUsage/ts/public/types";
import { SuccessfulPayment } from "../molecules/SuccessfulPayment";
import { formatCurrency } from "../../../utils/formatHelpers";
import { PaymentIFrame } from "./PaymentIframeContainer";
import {
  MessageType,
  Outbound_PaymentSuccess_MessagePayload,
  PayMethod,
} from "checkout/types/payments-ui-form";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { Mixpanel } from "providers/mixpanel";
import { CUSTOMER_SELF_CHECKOUT__PAYMENT_SUCCESS_ON_PAY_DEPOSIT_TAB_IN_CHECKOUT } from "constants/mixpanelEvents";
import { useAuthToken } from "hooks/useAuthToken";

type DepositPaymentIFrameProps = {
  paymentMethod: PayMethod;
  paymentAmount: number;
  prospect: SplatProspectResponse;
  contacts: SplatContact[];
  cancelPayment: () => void;
  paymentResults?: Outbound_PaymentSuccess_MessagePayload;
  setPaymentResults: (payload: Outbound_PaymentSuccess_MessagePayload) => void;
};

const DepositPaymentIFrame: FC<DepositPaymentIFrameProps> = ({
  paymentAmount,
  paymentMethod,
  prospect,
  contacts,
  cancelPayment,
  paymentResults,
  setPaymentResults,
}) => {
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const isCustomer = !!proposalToken;

  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const { refreshTasks, proposalId, prospectId } = useCheckoutContext();

  const messageHandler = useCallback(
    (message: MessageEvent) => {
      const { payload, type } = message?.data;
      if (type === MessageType.OUTBOUND_PAYMENT_SUCCESS) {
        if (isCustomer) {
          Mixpanel.track(
            CUSTOMER_SELF_CHECKOUT__PAYMENT_SUCCESS_ON_PAY_DEPOSIT_TAB_IN_CHECKOUT,
            { proposalId, prospectId }
          );
        }
        setPaymentResults(payload);
        setShowSuccessSnackbar(true);
        refreshTasks();
      }
    },
    [refreshTasks, setPaymentResults, isCustomer, proposalId, prospectId]
  );

  useEffect(() => {
    window.addEventListener("message", messageHandler);

    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, [messageHandler]);

  return (
    <>
      {paymentResults && prospect && (
        <SuccessfulPayment
          autoPay={false}
          paymentAmount={formatCurrency(paymentAmount)}
          customerEmail={prospect?.customerEmail ?? ""}
          paymentType={paymentMethod}
          lastFour={paymentResults?.lastFour ?? ""}
        />
      )}
      {!paymentResults && prospect && contacts.length > 0 && (
        <PaymentIFrame
          autopay={false}
          paymentMethod={paymentMethod}
          paymentAmount={paymentAmount}
          prospect={prospect}
          contacts={contacts}
          cancelPayment={cancelPayment}
        />
      )}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        colorScheme="light"
        message={`Your payment has been successfully processed`}
        onClose={() => setShowSuccessSnackbar(false)}
        type="success"
      />
    </>
  );
};

export { DepositPaymentIFrame };
